import { z } from 'zod';
import { isAfter, isBefore, add } from 'date-fns';

import { TimeOffsetUnit, TimeOffsetRelation } from 'constants/enums';
import { EditableEvent } from 'types/event.types';
import { parseUTCDate, calculateScheduleTime } from 'utils/dateTimeUtils';
import { getPendingScheduledPublishAt } from '@event-editor/utils';

const selfCheckinModalSchema = z.object({
  allowUserCheckin: z.boolean(),
  schedule: z.object({
    quantity: z
      .number({ invalid_type_error: 'Send time is required' })
      .positive({ message: 'Send time must be a positive number' }),
    unit: z.nativeEnum(TimeOffsetUnit).transform((data) => data.toLowerCase()),
    relation: z.nativeEnum(TimeOffsetRelation)
  }),
  sendNotificationSettings: z.object({
    email: z.coerce.boolean(),
    slack: z.coerce.boolean()
  })
});

export const getSelfCheckinModalSchema = (event: EditableEvent) => {
  return selfCheckinModalSchema.superRefine((data, ctx) => {
    if (!data.allowUserCheckin) return;

    const { schedule } = data;
    const eventEndTimeUTC = event.datetimeEnd ? parseUTCDate(event.datetimeEnd) : null;
    const calculatedSendCheckinAt = event.datetimeScheduled
      ? calculateScheduleTime(
          parseUTCDate(event.datetimeScheduled),
          schedule.quantity,
          schedule.unit,
          schedule.relation === TimeOffsetRelation.BEFORE
        )
      : null;

    // Self check-in must be before 30 days after the event's end time
    if (
      calculatedSendCheckinAt &&
      eventEndTimeUTC &&
      isAfter(calculatedSendCheckinAt, add(eventEndTimeUTC, { days: 30 }))
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Self check-in must be before 30 days after the event's end time",
        path: ['schedule.quantity']
      });
    }

    // Self check-in must be after the event's scheduled publish time
    const publishAt = getPendingScheduledPublishAt(event.publishSchedule);
    if (calculatedSendCheckinAt && publishAt) {
      const publishAtDate = parseUTCDate(publishAt);
      if (isBefore(calculatedSendCheckinAt, publishAtDate)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Scheduled time must be after the event is scheduled to be published',
          path: ['schedule.quantity'],
          fatal: true
        });
      }
    }

    // Validate notification settings
    if (!data.sendNotificationSettings.email && !data.sendNotificationSettings.slack) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'You must have one notification method selected',
        path: ['sendNotificationSettings']
      });
    }
  });
};

export type SelfCheckinModalFormValues = z.infer<typeof selfCheckinModalSchema>;
